import Ticket from "../../interfaces/ticket/Ticket";
import { NewTicketRequest } from "../../interfaces/ticket/NewTicketRequest";
import { User } from "../../interfaces/User";
import Tag from "../../interfaces/tag/Tag";
import { ReorderRequest } from "../../interfaces/ticket/ReorderRequest";
import { SubmitBugRequest } from "../../interfaces/ticket/SubmitBugRequest";
import Sprint from "../../interfaces/sprint/Sprint";
import { getActiveSprint } from "../sprintServices/SprintService";

// Function to fetch a ticket by its ID
export async function getTicketById(
  ticketId: string,
  authToken: string,
  includeComments: boolean = false,
  includeAttachmentUrls: boolean = false
): Promise<Ticket | null> {
    const queryParams = new URLSearchParams();
    if (includeComments) queryParams.append('includeComments', 'true');
    if (includeAttachmentUrls) queryParams.append('includeAttachmentUrls', 'true');

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}?${queryParams.toString()}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error(`Failed to fetch ticket ${ticketId}:`, response.status, response.statusText);
            return null;
        }

        const res =  await response.json();
        console.log('Ticket', res);
        return res;
    } catch (error) {
        console.error(`Error fetching ticket ${ticketId}:`, error);
        return null;
    }
}

// Function to fetch all tickets with optional filtering
export async function getTickets(
  authToken: string,
  filters: { status?: string; sprintId?: string | null; search?: string; skip?: number; take?: number; }
): Promise<{ tickets: Ticket[]; totalCount: number; } | null> {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.sprintId) queryParams.append('sprintId', filters.sprintId);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.skip) queryParams.append('skip', filters.skip.toString());
    if (filters.take) queryParams.append('take', filters.take.toString());

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets?${queryParams.toString()}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch tickets:", response.status, response.statusText);
            return null;
        }

        const tickets = await response.json();
        const ticketsList = {
            tickets: tickets.items,
            totalCount: tickets.totalCount,
        };
        return ticketsList
    } catch (error) {
        console.error("Error fetching tickets:", error);
        return null;
    }
}

// Function to create a new ticket
export async function createTicket(
  authToken: string,
  newTicket: NewTicketRequest
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(newTicket),
        });

        if (!response.ok) {
            // Handle non-201 responses
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error creating ticket:", error);
        return null;
    }
}

export async function submitBug(
    authToken: string,
    bugReport: SubmitBugRequest
  ): Promise<Ticket | null> {
      const formData = new FormData();
      formData.append('title', bugReport.title);
      if (bugReport.description) formData.append('description', bugReport.description);
      if (bugReport.pageUrl) formData.append('pageUrl', bugReport.pageUrl);
      if (bugReport.userAgent) formData.append('userAgent', bugReport.userAgent);
      if (bugReport.appVersion) formData.append('appVersion', bugReport.appVersion);
      if (bugReport.os) formData.append('os', bugReport.os);
      if (bugReport.viewport) formData.append('viewport', bugReport.viewport);
      if (bugReport.extra) formData.append('extra', bugReport.extra);
      
      if (bugReport.files) {
          bugReport.files.forEach(file => {
              formData.append('files', file);
          });
      }
  
      try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/submit-bug`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${authToken}`,
              },
              body: formData,
          });
  
          if (!response.ok) {
              const errorBody = await response.text();
              console.error("Failed to submit bug:", response.status, response.statusText, errorBody);
              return null;
          }
  
          return await response.json();
      } catch (error) {
          console.error("Error submitting bug:", error);
          return null;
      }
  }

// Function to update an existing ticket
export async function updateTicket(
  authToken: string,
  ticketId: string,
  updates: Partial<NewTicketRequest> // Use a specific update interface if available
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error(`Error updating ticket ${ticketId}:`, error);
        return null;
    }
}

export async function updateTicketAssignee(
  authToken: string,
  ticketId: string,
  assigneeId: string | null
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/assignee`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ assigneeId }),
        });

        if (!response.ok) {
            console.error("Failed to update ticket assignee:", response.status, response.statusText);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error updating ticket assignee:", error);
        return false;
    }
}

// Function to delete a ticket by its ID
export async function deleteTicket(
  authToken: string,
  ticketId: string
): Promise<boolean> {
  console.log(`Deleting ticket with ID: ${ticketId}`);
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 200));
  return false;
}

export async function getAssignableUsers(authToken: string): Promise<User[] | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/users/admin-or-developer`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch assignable users:", response.status, response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching assignable users:", error);
        return null;
    }
}

export async function getTicketTags(authToken: string, search?: string, take?: number): Promise<Tag[] | null> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (take) queryParams.append('take', take.toString());

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/tags?${queryParams.toString()}`, {
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch ticket tags:", response.status, response.statusText);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching ticket tags:", error);
        return null;
    }
}

export async function reorderTickets(authToken: string, payload: ReorderRequest): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/reorder`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.error("Failed to reorder tickets:", response.status, response.statusText);
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error reordering tickets:", error);
        return false;
    }
}

export async function closeTicket(
    authToken: string,
    ticketId: string,
    comment: string
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/close`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ comment }),
        });

        if (!response.ok) {
            console.error("Failed to close ticket:", response.status, response.statusText);
            return false;
        }
        return true;
    } catch (error) {
        console.error("Error closing ticket:", error);
        return false;
    }
}


export async function addTagToTicket(
    authToken: string,
    ticketId: string,
    tagId: string
): Promise<boolean> {

    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/tags`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ tagId }),
        });

        if (!response.ok) {
            console.error("Failed to add tag to ticket:", response.status, response.statusText);
            return false;
        }
        return true;
    } catch (error) {
        console.error("Error adding tag to ticket:", error);
        return false;
    }
}



export async function removeTagFromTicket(
    authToken: string,
    ticketId: string,
    tagId: string
): Promise<boolean> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/tags/${tagId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error("Failed to remove tag from ticket:", response.status, response.statusText);
            return false;
        }
        return true;

    } catch (error) {
        console.error("Error removing tag from ticket:", error);
        return false;
    }
}

export async function updateTicketStoryPoints(
    authToken: string,
    ticketId: string,
    storyPoints: number | null
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/story-points`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ storyPoints }),
        });

        if (!response.ok) {
            console.error("Failed to update story points:", response.status, response.statusText);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("Error updating story points:", error);
        return null;
    }
}

export async function updateTicketPriority(
    authToken: string,
    ticketId: string,
    priority: string
): Promise<Ticket | null> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tickets/${ticketId}/priority`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
            },
            body: JSON.stringify({ priority }),
        });

        if (!response.ok) {
            console.error("Failed to update priority:", response.status, response.statusText);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("Error updating priority:", error);
        return null;
    }
}
